import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMobilePrefs } from "@/mobile/ui/MobilePrefs";
import { useChrome } from "@/mobile/ui/MobileShell";
import PlayWalletSelector from "@/components/ui/PlayWalletSelector";
import api from "@/lib/api";
import { ORIGINAL_PAGE_ROUTES } from "@/casino/nativeGames";
import { buildCasinoGames, GAME_LABEL } from "@/pages/Casino";

/* MobileCasinoGame — the mobile host for the NATIVE PRYMEBET Originals + native live
   tables. The actual game components live in pages/Casino.jsx (money-path, reused
   verbatim); desktop renders them in its own `active` host. On mobile, tapping a
   native Original/live table deep-links to /casino/play/:slug, which lands here so
   the game actually opens (previously it routed to /casino?game=… which the mobile
   lobby stripped, so only Mines — on its own route — worked). Mines and Dice use their own
   dedicated screens. */
export default function MobileCasinoGame() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const liveTable = sp.get("table") || null;  // e.g. /casino/play/liveblackjack?table=breakout
  const navigate = useNavigate();
  const { user, refresh, streamerMode } = useAuth();
  const prefs = useMobilePrefs();
  const currency = prefs?.playCurrency || "coins";
  const [edges, setEdges] = useState({});
  const [gated, setGated] = useState([]);

  useChrome({ mode: "back", title: GAME_LABEL[slug] || "Game" });

  useEffect(() => {
    api.get("/casino/house-edges")
      .then(({ data }) => { setEdges(data.edges || {}); setGated(data.currency_gated || []); })
      .catch(() => {});
  }, []);

  // Dedicated Originals share one full game page on desktop and mobile.
  if (ORIGINAL_PAGE_ROUTES[slug]) return <Navigate to={ORIGINAL_PAGE_ROUTES[slug]} replace />;

  const onResult = async () => { await refresh(); };
  const edgeFor = (id) => {
    const e = edges[id];
    if (e == null) return null;
    if (gated.includes(id)) return currency === "cash" ? e : 0;
    return e;
  };

  // Shared source of truth with the desktop Casino host — a new native game added in
  // buildCasinoGames opens here automatically (no second place to update).
  const games = buildCasinoGames({ onResult, currency, edgeFor, liveTable });

  const node = games[slug];
  // Unknown slug (or a provider game that reached here by mistake) → back to the lobby.
  // Loudly flag it in dev so a mis-wired new game is caught immediately, not silently.
  if (!node) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error(`[MobileCasinoGame] no native game host for slug "${slug}" — add it to buildCasinoGames in pages/Casino.jsx`);
    }
    return <Navigate to="/casino" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-4 py-5">
      <div className="mb-5">
        <PlayWalletSelector
          value={currency}
          onChange={prefs?.setPlayCurrency}
          coins={user?.coins}
          cash={user?.cash}
          bonus={user?.bonus}
          mask={(s) => (streamerMode ? "•••" : s)}
          caption={false}
          className="w-full"
        />
      </div>

      {node}

      {edges[slug] != null && (
        <div className="mt-5 border-t border-[#27272A] pt-3 text-sm text-[#a1a1aa]" data-testid={`game-edge-${slug}`}>
          {gated.includes(slug) ? (
            <span><b className="text-white">{edges[slug]}%</b> house edge on PCash</span>
          ) : (
            <span><b className="text-white">{edges[slug]}%</b> — applies to all play</span>
          )}
        </div>
      )}
    </div>
  );
}
